import {
  type Equipment,
  POWER_GROUP_LABELS,
  PRINTER_STATUS_LABELS,
  Printer,
  PrinterList,
  PrinterStatus,
  formatBRL,
  formatIsoDate,
} from '@deizi/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { Icon } from '../../components/Icon';
import { PageHead } from '../../components/PageHead';
import { Alert, EmptyState, ErrorBox } from '../../components/States';
import { useToast } from '../../components/Toast';
import { api } from '../../lib/api';
import { errorMessage } from '../../lib/messages';
import { useEquipment } from '../../lib/expenses-api';
import { decimalInput, formatHm } from '../../lib/numbers';
import { secondsLeft, useQueue, whenText } from '../../lib/queue-api';
import { useIsAdmin } from '../../lib/session';
import { useSettings } from '../../lib/settings-api';
import {
  LogDialog,
  MaintenanceCard,
  MaterialsCard,
  RetireDialog,
  RetiredList,
  SlotsCard,
  StatCards,
  SuggestedMaintenance,
  useOverview,
} from './PrinterExtras';
import { PrinterDialog } from './PrinterDialog';

const printersKey = ['printers'] as const;

const STATUS_BADGE: Record<PrinterStatus, string> = {
  idle: 'badge ok',
  printing: 'badge info',
  maintenance: 'badge warn',
  out_of_service: 'badge err',
};

function CostCard({ printer }: { printer: Printer }) {
  const settings = useSettings();
  const tariff = settings.data?.costs.energyTariffPerKwh ?? null;
  const bandeira = settings.data?.costs.bandeiraPerKwh ?? 0;
  const { low, high } = printer.costPerHour;
  return (
    <section className="card col" style={{ gap: 12 }} aria-live="polite">
      <h2>Custo por hora</h2>
      <div className="kv">
        <span>Depreciação</span>
        <span className="v">{formatBRL(low.depreciation)}</span>
      </div>
      <p className="xs muted" style={{ marginTop: -6 }}>
        {formatBRL(printer.purchasePriceCentavos)} ÷ {printer.lifetimeHours.toLocaleString('pt-BR')}{' '}
        h
      </p>
      <div className="kv">
        <span>
          Energia · {POWER_GROUP_LABELS.low} {printer.powerLowWatts} W
        </span>
        <span className="v">{low.energy === null ? '—' : formatBRL(low.energy)}</span>
      </div>
      <p className="xs muted" style={{ marginTop: -6 }}>
        {tariff === null
          ? 'Tarifa de energia ainda não informada.'
          : `${decimalInput(printer.powerLowWatts / 1000, 3, 2)} kW × R$ ${decimalInput(tariff + bandeira, 5, 2)}/kWh`}
      </p>
      <div className="kv">
        <span>Manutenção (estimada)</span>
        <span className="v">{formatBRL(low.maintenance)}</span>
      </div>
      <div className="kv" style={{ borderTop: '1px solid var(--line)', paddingTop: 10 }}>
        <span style={{ fontWeight: 600 }}>Total {POWER_GROUP_LABELS.low}</span>
        <span className="v fr" style={{ fontSize: 20, color: 'var(--pe)' }}>
          {formatBRL(low.total)}
        </span>
      </div>
      <p className="sm muted">
        {POWER_GROUP_LABELS.high} ({printer.powerHighWatts} W):{' '}
        <b className="tab">{formatBRL(high.total)}</b>
        {high.energy !== null ? ` · energia ${formatBRL(high.energy)}` : ''}
      </p>
      {tariff === null ? (
        <Alert tone="warn">
          Sem a tarifa de energia o custo por hora fica sem a energia.{' '}
          <Link to="/configuracoes/custos?aba=custos">Informar a tarifa</Link>
        </Alert>
      ) : (
        <p className="xs muted">
          A depreciação pesa muito mais no preço do que a tarifa: a vida útil é o número que mais
          importa.
        </p>
      )}
    </section>
  );
}

function CapabilitiesCard({ printer, onEdit }: { printer: Printer; onEdit?: () => void }) {
  const rows: [string, string | null][] = [
    ['Volume', printer.buildVolume],
    ['Bicos', printer.nozzles],
    [
      'Multicolor',
      printer.multicolorUnit
        ? `${printer.multicolorUnit}${printer.multicolorSlots ? ` · ${printer.multicolorSlots} slots` : ''}`
        : null,
    ],
    ['Câmara', printer.enclosure],
    ['Materiais', printer.materials.length > 0 ? printer.materials.join(', ') : null],
  ];
  return (
    <section className="card col" style={{ gap: 10 }}>
      <div className="row between">
        <h2>Capacidades</h2>
        {onEdit ? (
          <button type="button" className="btn sm" onClick={onEdit}>
            Editar
          </button>
        ) : null}
      </div>
      {rows.map(([k, v]) => (
        <div key={k} className="kv">
          <span className="muted">{k}</span>
          <span
            className="v"
            style={{
              whiteSpace: 'normal',
              textAlign: 'right',
              fontWeight: v ? 500 : 400,
              color: v ? undefined : 'var(--ink2)',
            }}
          >
            {v ?? 'conferir na ficha técnica'}
          </span>
        </div>
      ))}
    </section>
  );
}

/** Impressoras-Celular: the current print and the next one, from the queue. */
function PrinterNow({ printer }: { printer: Printer }) {
  const queue = useQueue();
  const jobs = (queue.data?.data ?? []).filter((j) => j.printer?.id === printer.id);
  const current = jobs.find((j) => j.status === 'printing');
  const next = jobs.find((j) => j.status === 'queued');
  const [now] = useState(() => Date.now());
  if (!queue.data || (!current && !next)) return null;
  return (
    <Link to="/fila" className="card col" style={{ gap: 8, color: 'inherit' }}>
      {current ? (
        <>
          <div className="row between" style={{ gap: 8 }}>
            <b className="trunc">
              {current.label}
              {current.filaments.length
                ? ` · ${[...new Set(current.filaments.map((f) => f.filament.name))].join(' + ')}`
                : ''}
            </b>
            <span className="badge info">Imprimindo</span>
          </div>
          <div className="layers thin">
            <div className="fill" style={{ width: `${current.progressPct ?? 0}%` }} />
            <div className="nozzle" style={{ left: `${current.progressPct ?? 0}%` }} />
          </div>
          <div className="row between sm">
            <span>
              {current.progressPct ?? 0}% · faltam {formatHm(secondsLeft(current, now))}
            </span>
            <span className="muted">
              {current.expectedEndAt ? whenText(current.expectedEndAt, 'termina ') : ''}
            </span>
          </div>
        </>
      ) : (
        <span className="sm">{printer.name} livre.</span>
      )}
      {next ? (
        <div
          className="row between sm"
          style={{ borderTop: '1px solid var(--line)', paddingTop: 8 }}
        >
          <span className="muted">Próxima</span>
          <span className="trunc">
            {next.label} · {formatHm(next.estSeconds)}
          </span>
        </div>
      ) : null}
    </Link>
  );
}

/** The most overdue maintenance task, with "Marcar feita" (Impressoras-Celular). */
function OverdueTask({ printer }: { printer: Printer }) {
  const overview = useOverview(printer.id).data;
  const [logging, setLogging] = useState<string | null>(null);
  const due = overview?.tasks
    .filter((t) => t.state === 'due')
    .toSorted((a, b) => a.dueInHours - b.dueInHours)[0];
  const soon = overview?.tasks.toSorted((a, b) => a.dueInHours - b.dueInHours)[0];
  if (!overview || !soon) return null;
  return (
    <>
      {due ? (
        <div className="alert err">
          <Icon name="alert" className="ic" />
          <div className="grow sm">
            <b>{due.name}</b> atrasada {Math.round(-due.dueInHours)} h
          </div>
          <button type="button" className="btn sm" onClick={() => setLogging(due.id)}>
            Marcar feita
          </button>
        </div>
      ) : (
        <div className="alert ok">
          <Icon name="check" className="ic" />
          <div className="grow sm">
            Próxima manutenção: <b>{soon.name}</b> em {Math.max(0, Math.round(soon.dueInHours))} h.
          </div>
        </div>
      )}
      {logging ? (
        <LogDialog printer={printer} taskId={logging} onClose={() => setLogging(null)} />
      ) : null}
    </>
  );
}

/** "10 × R$ 1.249,90 a partir de 17/10/2026" from the printer's equipment plan. */
function installmentsText(equipment: Equipment[] | undefined, printerId: string): string | null {
  const plan = equipment?.find((e) => e.printer?.id === printerId)?.installments ?? [];
  if (!plan.length) return null;
  const first = plan.toSorted((a, b) => a.dueOn.localeCompare(b.dueOn))[0]!;
  const amount = plan.at(-1)!.amountCentavos;
  return `${plan.length} × ${formatBRL(amount)} a partir de ${formatIsoDate(first.dueOn)}`;
}

function PrinterDetail({
  printer,
  isAdmin,
  onEdit,
}: {
  printer: Printer;
  isAdmin: boolean;
  onEdit: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const status = useMutation({
    mutationFn: (s: PrinterStatus) =>
      api.post(`/printers/${printer.id}/status`, Printer, { status: s }),
    onSuccess: (p) => {
      void qc.invalidateQueries({ queryKey: printersKey });
      toast(`${p.name}: ${PRINTER_STATUS_LABELS[p.status]}`);
    },
    onError: (err) => toast(errorMessage(err)),
  });
  const [retiring, setRetiring] = useState(false);
  const overview = useOverview(printer.id).data;
  const equipment = useEquipment();
  const plan = installmentsText(equipment.data?.data, printer.id);
  return (
    <>
      {retiring ? <RetireDialog printer={printer} onClose={() => setRetiring(false)} /> : null}
      <div className="pagehead">
        <div>
          <div className="crumb">Produzir / Impressoras</div>
          <div className="row wrap" style={{ gap: 12 }}>
            <h1>{printer.name}</h1>
            <span className={STATUS_BADGE[printer.status]}>
              {PRINTER_STATUS_LABELS[printer.status]}
            </span>
            {overview ? (
              <span
                className={
                  overview.tasks.some((t) => t.state === 'due')
                    ? 'badge err'
                    : overview.alerts
                      ? 'badge warn'
                      : 'badge ok'
                }
              >
                {overview.alerts
                  ? `${overview.alerts} ${overview.alerts === 1 ? 'alerta' : 'alertas'} de manutenção`
                  : 'Manutenção em dia'}
              </span>
            ) : null}
          </div>
          <p className="sm muted">
            {printer.model}
            {printer.purchaseDate ? ` · comprada em ${formatIsoDate(printer.purchaseDate)}` : ''}
            {plan ? ` · ${plan}` : ''}
          </p>
        </div>
        <div className="row wrap" style={{ gap: 10, alignItems: 'flex-end' }}>
          <div className="field" style={{ minWidth: 170 }}>
            <label htmlFor="stsel" className="xs">
              Situação
            </label>
            <select
              id="stsel"
              className="input"
              value={printer.status}
              onChange={(e) => {
                const next = PrinterStatus.options.find((s) => s === e.target.value);
                if (next) status.mutate(next);
              }}
            >
              {PrinterStatus.options.map((s) => (
                <option key={s} value={s}>
                  {PRINTER_STATUS_LABELS[s]}
                </option>
              ))}
            </select>
          </div>
          {isAdmin ? (
            <>
              <button type="button" className="btn" onClick={onEdit}>
                Editar
              </button>
              <button type="button" className="btn g" onClick={() => setRetiring(true)}>
                Aposentar
              </button>
            </>
          ) : null}
        </div>
      </div>
      <OverdueTask printer={printer} />
      <PrinterNow printer={printer} />
      <StatCards printer={printer} />
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 22, alignItems: 'flex-start' }}>
        <div className="col" style={{ flex: '3 1 420px', minWidth: 0, gap: 18 }}>
          <CapabilitiesCard printer={printer} onEdit={isAdmin ? onEdit : undefined} />
          <MaintenanceCard printer={printer} />
          <MaterialsCard printer={printer} />
        </div>
        <aside className="col" style={{ flex: '2 1 320px', minWidth: 0, gap: 18 }}>
          <CostCard printer={printer} />
          <SuggestedMaintenance printer={printer} />
          <SlotsCard printer={printer} />
        </aside>
      </div>
    </>
  );
}

/** Produzir · Impressoras (design Impressora-Detalhe / Impressoras-Celular). */
export function PrintersPage() {
  const isAdmin = useIsAdmin();
  const qc = useQueryClient();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const [dialog, setDialog] = useState<'new' | 'edit' | null>(null);
  const list = useQuery({
    queryKey: printersKey,
    queryFn: () => api.get('/printers?limit=200', PrinterList),
  });
  const printers = list.data?.data ?? [];
  const selected = printers.find((p) => p.id === params.get('id')) ?? printers[0] ?? null;

  return (
    <>
      {list.isPending ? (
        <div className="col" style={{ gap: 18 }} aria-busy="true">
          <div className="skel" style={{ height: 60 }} />
          <div className="skel" style={{ height: 120 }} />
          <div className="skel" style={{ height: 240 }} />
        </div>
      ) : null}
      {list.isError ? (
        <>
          <PageHead crumb="Produzir" title="Impressoras" />
          <div className="card">
            <ErrorBox
              title="Não foi possível carregar as impressoras"
              onRetry={() => void list.refetch()}
            />
          </div>
        </>
      ) : null}
      {list.isSuccess && printers.length === 0 ? (
        <>
          <PageHead crumb="Produzir" title="Impressoras" />
          <div className="card">
            <EmptyState
              title="Nenhuma impressora ainda"
              action={
                isAdmin ? (
                  <button type="button" className="btn p" onClick={() => setDialog('new')}>
                    Adicionar impressora
                  </button>
                ) : null
              }
            >
              Cadastre a sua impressora para calcular o custo por hora e organizar a fila.
            </EmptyState>
          </div>
        </>
      ) : null}
      {selected ? (
        <>
          <div className="row wrap" style={{ gap: 10 }}>
            <span className="lbl">Impressoras</span>
            <div className="seg" role="tablist" aria-label="Trocar impressora">
              {printers.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  role="tab"
                  aria-selected={p.id === selected.id}
                  className={p.id === selected.id ? 'on' : ''}
                  onClick={() => setParams({ id: p.id }, { replace: true })}
                >
                  {p.name}
                </button>
              ))}
            </div>
            {isAdmin ? (
              <button type="button" className="btn sm" onClick={() => setDialog('new')}>
                <Icon name="plus" size={16} />
                Nova impressora
              </button>
            ) : null}
          </div>
          <PrinterDetail
            key={selected.id}
            printer={selected}
            isAdmin={isAdmin}
            onEdit={() => setDialog('edit')}
          />
        </>
      ) : null}
      {list.isSuccess ? <RetiredList /> : null}
      {dialog ? (
        <PrinterDialog
          printer={dialog === 'edit' ? selected : null}
          onClose={() => setDialog(null)}
          onSaved={(p) => {
            setDialog(null);
            void qc.invalidateQueries({ queryKey: printersKey });
            setParams({ id: p.id }, { replace: true });
            toast(
              `Impressora salva. Custo por hora: ${formatBRL(p.costPerHour.low.total)} com PLA.`,
            );
          }}
        />
      ) : null}
    </>
  );
}
